/**
 * 🎨 Gestionnaire de Thèmes Saisonniers
 * 
 * Ajouter un nouveau thème :
 * 1. Ajouter une entrée dans SEASONAL_THEMES avec les dates
 * 2. Créer le fichier CSS correspondant dans styles/themes/
 * 3. C'est tout ! Le thème s'active et se désactive automatiquement.
 * 
 * Les thèmes sont évalués par priorité (plus haut = prioritaire).
 */

export interface SeasonalTheme {
  id: string;
  name: string;
  /** CSS class applied to <body> */
  cssClass: string;
  /** Month (1-12) and day to start (inclusive) */
  startMonth: number;
  startDay: number;
  /** Month (1-12) and day to end (exclusive — theme stops before this date) */
  endMonth: number;
  endDay: number;
  /** Higher = checked first when dates overlap */
  priority: number;
  /** Show floating particles? */
  particles?: {
    enabled: boolean;
    emojis: string[];
    count: number;
    /** Speed multiplier (1 = default) */
    speed?: number;
  };
  /** Override CSS variables */
  cssVars?: Record<string, string>;
}

// ─── THEMES REGISTRY ─────────────────────────────────────
export const SEASONAL_THEMES: SeasonalTheme[] = [
  {
    id: 'valentine',
    name: 'Saint-Valentin',
    cssClass: 'theme-valentine',
    startMonth: 2,  // 1er février
    startDay: 1,
    endMonth: 3,    // jusqu'au 28 février (< 1er mars)
    endDay: 1,
    priority: 10,
    particles: {
      enabled: true,
      emojis: ['❤️', '💕', '💖', '💘', '💗', '🌹', '✨'],
      count: 20,
      speed: 0.7,
    },
    cssVars: {
      '--seasonal-primary': '#e91e63',
      '--seasonal-secondary': '#f48fb1',
      '--seasonal-accent': '#ff6090',
      '--seasonal-glow': 'rgba(233, 30, 99, 0.3)',
      '--seasonal-bg-gradient': 'linear-gradient(135deg, #1a0a12 0%, #10111A 30%, #1a0a1e 100%)',
    },
  },
  {
    id: 'halloween',
    name: 'Halloween',
    cssClass: 'theme-halloween',
    startMonth: 10,  // 15 octobre
    startDay: 15,
    endMonth: 11,    // jusqu'au 2 novembre
    endDay: 3,
    priority: 10,
    particles: {
      enabled: true,
      emojis: ['🎃', '👻', '🦇', '🕷️', '💀', '🕸️'],
      count: 15,
      speed: 0.5,
    },
    cssVars: {
      '--seasonal-primary': '#ff6600',
      '--seasonal-secondary': '#8b00ff',
      '--seasonal-accent': '#ff9800',
      '--seasonal-glow': 'rgba(255, 102, 0, 0.3)',
      '--seasonal-bg-gradient': 'linear-gradient(135deg, #1a1005 0%, #10111A 30%, #0d0a1a 100%)',
    },
  },
  {
    id: 'christmas',
    name: 'Noël',
    cssClass: 'theme-christmas',
    startMonth: 12,  // 1er décembre
    startDay: 1,
    endMonth: 1,     // jusqu'au 6 janvier (Épiphanie)
    endDay: 7,
    priority: 10,
    particles: {
      enabled: true,
      emojis: ['❄️', '🎄', '⭐', '🎅', '🎁', '✨'],
      count: 18,
      speed: 0.4,
    },
    cssVars: {
      '--seasonal-primary': '#c62828',
      '--seasonal-secondary': '#2e7d32',
      '--seasonal-accent': '#ffd54f',
      '--seasonal-glow': 'rgba(198, 40, 40, 0.3)',
      '--seasonal-bg-gradient': 'linear-gradient(135deg, #0a1210 0%, #10111A 30%, #1a0a0a 100%)',
    },
  },
  {
    id: 'easter',
    name: 'Pâques',
    cssClass: 'theme-easter',
    startMonth: 4,   // 2 avril
    startDay: 2,
    endMonth: 5,     // jusqu'au 30 avril (exclusif = 1er mai)
    endDay: 1,
    priority: 10,
    particles: {
      enabled: true,
      emojis: ['🥚', '🌼', '🌸', '🐣', '🌷', '🐰'],
      count: 16,
      speed: 0.6,
    },
    cssVars: {
      '--seasonal-primary': '#fde68a',
      '--seasonal-secondary': '#fbbf24',
      '--seasonal-accent': '#a7c4a0',
      '--seasonal-glow': 'rgba(253, 230, 138, 0.18)',
      '--seasonal-bg-gradient': 'linear-gradient(160deg, #160f00 0%, #120d00 40%, #160c00 100%)',
    },
  },
  {
    id: 'april-fools',
    name: 'Poisson d\'Avril',
    cssClass: 'theme-april-fools',
    startMonth: 4,   // 1er avril uniquement
    startDay: 1,
    endMonth: 4,
    endDay: 2,
    priority: 20,    // Passe devant Pâques ce jour-là
    particles: {
      enabled: true,
      emojis: ['🐟', '🐠', '🐡', '🎣', '🐟', '🐠'],
      count: 20,
      speed: 0.9,
    },
  },
  // 🎆 Ajoute ici d'autres thèmes : Été, Rentrée, etc.
];

// ─── HELPER ──────────────────────────────────────────────

/**
 * Check if a date falls within a theme's active period.
 * Handles cross-year ranges (e.g. Dec 1 → Jan 6).
 */
function isDateInRange(date: Date, theme: SeasonalTheme): boolean {
  const month = date.getMonth() + 1; // 1-12
  const day = date.getDate();

  const startVal = theme.startMonth * 100 + theme.startDay;
  const endVal = theme.endMonth * 100 + theme.endDay;
  const currentVal = month * 100 + day;

  if (startVal < endVal) {
    // Normal range (e.g. Feb 1 → Mar 1)
    return currentVal >= startVal && currentVal < endVal;
  } else {
    // Cross-year range (e.g. Dec 1 → Jan 7)
    return currentVal >= startVal || currentVal < endVal;
  }
}

/**
 * Returns the currently active seasonal theme (or null for default).
 * Evaluated by priority; highest priority wins.
 */
export function getActiveTheme(date: Date = new Date()): SeasonalTheme | null {
  const matching = SEASONAL_THEMES
    .filter(t => isDateInRange(date, t))
    .sort((a, b) => b.priority - a.priority);

  return matching.length > 0 ? matching[0] : null;
}

/**
 * Apply a seasonal theme's CSS variables to the document root.
 */
export function applyThemeVars(theme: SeasonalTheme | null): void {
  const root = document.documentElement;

  // Clear all seasonal vars first
  SEASONAL_THEMES.forEach(t => {
    if (t.cssVars) {
      Object.keys(t.cssVars).forEach(key => root.style.removeProperty(key));
    }
  });

  // Remove all theme classes
  SEASONAL_THEMES.forEach(t => document.body.classList.remove(t.cssClass));

  if (theme) {
    // Apply CSS vars
    if (theme.cssVars) {
      Object.entries(theme.cssVars).forEach(([key, val]) => root.style.setProperty(key, val));
    }
    // Apply CSS class
    document.body.classList.add(theme.cssClass);
  }
}
